import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  DepartmentView,
  RolloverPreviewView,
  RolloverResultView,
  SeasonView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import {
  canSubmitRollover,
  carriedMemberCount,
  categoryLabel,
  rolloverFormFrom,
  rolloverRequestBody,
  type RolloverForm,
} from './teams';

export interface RolloverSectionProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  season: SeasonView;
  department: DepartmentView;
  /** Called with the new draft season so the page can show it. */
  onRolledOver: (seasonId: string) => Promise<void>;
}

type ErrorKey = ApiRequestError['messageKey'];

/**
 * Proposes the next season from the one being viewed. The first step only reads; creating the new
 * draft season, teams and groups happens on submit and never changes the season being copied.
 */
export function RolloverSection({
  locale,
  base,
  csrfToken,
  season,
  department,
  onRolledOver,
}: RolloverSectionProps): React.JSX.Element {
  const [form, setForm] = useState<RolloverForm | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [result, setResult] = useState<RolloverResultView | null>(null);

  function failed(error: unknown): void {
    setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
  }

  async function open(): Promise<void> {
    setErrorKey(null);
    setResult(null);
    try {
      const preview = await apiRequest<RolloverPreviewView>(
        `${base}/seasons/${season.id}/rollover-preview`,
      );
      setForm(rolloverFormFrom(preview));
    } catch (error) {
      failed(error);
    }
  }

  async function submit(current: RolloverForm): Promise<void> {
    setErrorKey(null);
    try {
      const created = await apiRequest<RolloverResultView>(
        `${base}/seasons/${season.id}/rollover`,
        { method: 'POST', body: rolloverRequestBody(current), csrfToken },
      );
      setForm(null);
      setResult(created);
      await onRolledOver(created.season.id);
    } catch (error) {
      failed(error);
    }
  }

  return (
    <Box component="section" aria-label={translate(locale, 'teams.rollover.title')} sx={{ mt: 3 }}>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {result !== null && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {translate(locale, 'teams.rollover.done')} {result.season.label}:{' '}
          {String(result.teamsCreated)} {translate(locale, 'teams.rollover.teamsCreated')},{' '}
          {String(result.groupsCreated)} {translate(locale, 'teams.rollover.groupsCreated')},{' '}
          {String(result.placementsCreated)} {translate(locale, 'teams.rollover.placementsCreated')}
        </Alert>
      )}
      {form === null ? (
        <Button
          variant="outlined"
          onClick={() => {
            void open();
          }}
        >
          {translate(locale, 'teams.rollover.open')}
        </Button>
      ) : (
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(form);
          }}
        >
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
            {translate(locale, 'teams.rollover.title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {translate(locale, 'teams.rollover.intro')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              required
              size="small"
              label={translate(locale, 'teams.season.name')}
              value={form.label}
              onChange={(event) => {
                setForm({ ...form, label: event.target.value });
              }}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
            <TextField
              required
              size="small"
              type="date"
              label={translate(locale, 'teams.season.startsOn')}
              value={form.startsOn}
              onChange={(event) => {
                setForm({ ...form, startsOn: event.target.value });
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              required
              size="small"
              type="date"
              label={translate(locale, 'teams.season.endsOn')}
              value={form.endsOn}
              onChange={(event) => {
                setForm({ ...form, endsOn: event.target.value });
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {form.teams.map((team, index) => (
              <Box
                component="li"
                key={team.sourceTeamId}
                sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
              >
                <Checkbox
                  checked={team.selected}
                  slotProps={{ input: { 'aria-label': team.name } }}
                  onChange={(event) => {
                    setForm({
                      ...form,
                      teams: form.teams.map((entry, position) =>
                        position === index ? { ...entry, selected: event.target.checked } : entry,
                      ),
                    });
                  }}
                />
                <TextField
                  size="small"
                  label={translate(locale, 'teams.team.name')}
                  value={team.name}
                  disabled={!team.selected}
                  onChange={(event) => {
                    setForm({
                      ...form,
                      teams: form.teams.map((entry, position) =>
                        position === index ? { ...entry, name: event.target.value } : entry,
                      ),
                    });
                  }}
                />
                <TextField
                  select
                  size="small"
                  label={translate(locale, 'teams.team.category')}
                  value={team.categoryCode}
                  disabled={!team.selected}
                  onChange={(event) => {
                    setForm({
                      ...form,
                      teams: form.teams.map((entry, position) =>
                        position === index ? { ...entry, categoryCode: event.target.value } : entry,
                      ),
                    });
                  }}
                  sx={{ minWidth: 140 }}
                >
                  {department.categoryCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {categoryLabel(locale, code)}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'teams.rollover.members')}: {String(team.activeMembers)}
                </Typography>
              </Box>
            ))}
            {form.groups.map((group, index) => (
              <Box
                component="li"
                key={group.sourceGroupId}
                sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
              >
                <Checkbox
                  checked={group.selected}
                  slotProps={{ input: { 'aria-label': group.name } }}
                  onChange={(event) => {
                    setForm({
                      ...form,
                      groups: form.groups.map((entry, position) =>
                        position === index ? { ...entry, selected: event.target.checked } : entry,
                      ),
                    });
                  }}
                />
                <TextField
                  size="small"
                  label={translate(locale, 'teams.group.name')}
                  value={group.name}
                  disabled={!group.selected}
                  onChange={(event) => {
                    setForm({
                      ...form,
                      groups: form.groups.map((entry, position) =>
                        position === index ? { ...entry, name: event.target.value } : entry,
                      ),
                    });
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'teams.rollover.members')}: {String(group.activeMembers)}
                </Typography>
              </Box>
            ))}
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.carryOverMembers}
                onChange={(event) => {
                  setForm({ ...form, carryOverMembers: event.target.checked });
                }}
              />
            }
            label={`${translate(locale, 'teams.rollover.carryOver')} (${String(carriedMemberCount(form))})`}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button type="submit" variant="contained" disabled={!canSubmitRollover(form)}>
              {translate(locale, 'teams.rollover.submit')}
            </Button>
            <Button
              onClick={() => {
                setForm(null);
              }}
            >
              {translate(locale, 'teams.rollover.cancel')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
